import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Switch, Route, Redirect } from 'react-router-dom';
import feathers from '@feathersjs/feathers';
import socketio from '@feathersjs/socketio-client';
import io from 'socket.io-client';
import auth from '@feathersjs/authentication-client';

import { setWebsocketConnection } from './reducers/WebsocketReducer';
import { setUserAuthenticated, setUserData, logOut } from './reducers/UserReducer';
import * as serviceWorker from './serviceWorker';
import { notifyPanel } from './reducers/NotifierReducer';
import { getAccessPermission } from 'reducers/PermissionsReducer';

// Layout Blueprints
import { MinimalLayout, CadLayout } from './layout-blueprints';

// Pages
import PageLogin from './pages/PageLogin';
import Page404 from './pages/Page404';
import ResetPassword from 'pages/ResetPassword';
import ForcePasswordReset from 'pages/ForcePasswordReset';
import routeDefinitions, { defaultUrl } from './routeDefinitions';
import Loading from 'pages/Loading';
import { registerAppUpdate } from 'reducers/AppInfoReducer';
import { notify } from './reducers/NotifierReducer';
import Cookies from 'js-cookie';
import { hideSpinner, showSpinner } from 'reducers/UiReducer';

let reloadNeeded = false;
let registration = false;

const ProtectedRoute = ({ component: Component, isAuthenticated, ...rest }) => {
  if (!isAuthenticated && reloadNeeded) updateApp(registration);
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated === true ? (
          <Component {...props} />
        ) : (
          <Redirect to={{ pathname: '/Login', state: { from: props.location } }} />
        )
      }
    />
  );
};

const LoginRoute = ({ component: Component, isAuthenticated, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated === true ? (
          <Redirect to={{ pathname: defaultUrl, state: { from: props.location } }} />
        ) : (
          <Component {...props} />
        )
      }
    />
  );
};

const Routes = (props) => {
  const {
    setWebsocketConnection,
    setUserAuthenticated,
    setUserData,
    setLoggedInUserData,
    configLoaded,
    user,
    dictionary,
    permissions,
    userData,
    websocket,
  } = props;
  const { isAuthenticated, passExpirationDays, showPassChange } = user;
  const appPermission = getAccessPermission('modules', 'Incident Archive', 'any');
  const isLoaded = configLoaded && permissions.loaded && Object.entries(dictionary).length > 0;

  useEffect(() => {
    serviceWorker.register({
      onSuccess: () => console.log('service worker registered!'),
      onUpdate: () => {
        sessionStorage.setItem('pending_update', 'true');
      },
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const hasUpdate = sessionStorage.getItem('pending_update');
    if (hasUpdate !== 'true') return;
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      const waitingServiceWorker = reg.waiting;
      if (!waitingServiceWorker) return;
      waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
      waitingServiceWorker.onstatechange = (event) => {
        if (event.target.state === 'activated') {
          sessionStorage.removeItem('pending_update');
          window.location.reload();
        }
      };
    });
  }, [isAuthenticated]);

  useEffect(() => {
    async function establishConnection() {
      const client = feathers();
      client.configure(
        socketio(
          io(window.__RUNTIME_CONFIG__.REACT_APP_API_URL, {
            transports: ['websocket', 'polling'],
            perMessageDeflate: false,
            timeout: 20000,
            requestTimeout: 20000,
          })
        )
      );
      client.configure(auth({ storageKey: 'auth' }));
      try {
        const auth = Cookies.get('auth');
        if (auth) {
          window.localStorage.setItem('auth', auth);
          Cookies.remove('auth');
        }
        const user = await client.reAuthenticate();
        setWebsocketConnection(client);
        setUserData(user);
        setUserAuthenticated(true);
      } catch (error) {
        if (error.code === 401) {
          setUserAuthenticated(false);
          setUserData(null);
          setWebsocketConnection(client);
        }
        setWebsocketConnection(client);
      }
    }
    establishConnection();
    // eslint-disable-next-line
  }, [setWebsocketConnection, setUserAuthenticated, setLoggedInUserData]);

  const refreshToken = async () => {
    try {
      props.showSpinner();
      const currentToken = localStorage.getItem('auth');
      const response = await websocket.service('authentication').create({
        strategy: 'jwt',
        accessToken: currentToken,
        isRenewToken: true,
        AppName: 'ARCHIVE',
      });
      localStorage.setItem('auth', response.accessToken);
      websocket.set('authentication', response);
      setUserData(response);
      setUserAuthenticated(true);
      props.notify('Session extended!', 'success');
    } catch (error) {
      console.error('Refresh Failed', error);
      setUserAuthenticated(false);
    } finally {
      props.hideSpinner();
    }
  };

  const showWarning = (expiryTime) => {
    const expiryTimeText = new Date(expiryTime).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    props.notifyPanel(
      {
        title: 'Session Expiring Soon!',
        message: `Your session will expire at ${expiryTimeText}. Click CONTINUE to stay signed in.`,
        position: 'tc',
        level: 'warning',
        autoDismiss: props.sessionWarningMinutes * 60,
        action: {
          label: 'CONTINUE',
          callback: refreshToken,
        },
      },
      'warning'
    );
  };

  React.useEffect(() => {
    if (!isAuthenticated || !userData || !props.sessionWarningEnabled) return;
    const payload = userData?.authentication?.payload || userData?.payload;
    const exp = payload?.exp;
    if (!exp) return;
    const expiryTime = exp * 1000;
    const warningSeconds = Number(props.sessionWarningMinutes) * 60;
    const now = Date.now();
    const delay = expiryTime - now - warningSeconds * 1000;

    if (delay <= 0) {
      if (now < expiryTime) {
        showWarning(expiryTime);
      }
      return;
    }
    const timer = setTimeout(() => {
      showWarning(expiryTime);
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [isAuthenticated, userData, props.sessionWarningEnabled]);

  useEffect(() => {
    if (permissions.loaded && !appPermission) {
      props.notify('No sufficient permissions to run this app.', 'Error');
      props.logOut();
    }
  }, [permissions]);

  const renderRoutes = () => {
    if (!isLoaded) return <>Loading configuration, please wait...</>;
    return routeDefinitions.map((route, key) => {
      //let Layout = isLoaded && permission ? route.layout : MinimalLayout;
      //let component = isLoaded ? route.component : Loading;
      let Layout = route.layout;
      let component = route.component;
      //if (permission === false) component = NotAuthorized;
      return (
        <Route path={[route.url]} key={key}>
          <Layout>
            <ProtectedRoute
              path={route.url}
              component={component}
              isAuthenticated={isAuthenticated}
            />
          </Layout>
        </Route>
      );
    });
  };

  return (
    <Switch>
      <Redirect exact from="/" to="/Login" />
      <Route path={['/Login']}>
        <MinimalLayout>
          <LoginRoute path="/Login" component={PageLogin} isAuthenticated={isAuthenticated} />
        </MinimalLayout>
      </Route>
      <Route path={['/Reset-password']}>
        <MinimalLayout>
          <ResetPassword isAuthenticated={isAuthenticated} />
        </MinimalLayout>
      </Route>
      {passExpirationDays !== null && !showPassChange && renderRoutes()}
      {showPassChange && (
        <Route path={['/']}>
          <MinimalLayout>
            <ForcePasswordReset passExpirationDays={passExpirationDays} />
          </MinimalLayout>
        </Route>
      )}
      {passExpirationDays === null && (
        <Route path={['/']}>
          <MinimalLayout>
            <ProtectedRoute component={Loading} isAuthenticated={isAuthenticated} />
          </MinimalLayout>
        </Route>
      )}
      <Route>
        <CadLayout>
          <ProtectedRoute component={Page404} isAuthenticated={isAuthenticated} />
        </CadLayout>
      </Route>
    </Switch>
  );
};

const mapStateToProps = (state) => ({
  network: state.offline,
  user: state.user,
  configLoaded: state.config.loaded,
  dictionary: state.dictionary,
  permissions: state.permissions,
  userData: state.user.userData,
  websocket: state.websocket.websocket,
  sessionWarningEnabled: state.config.sessionWarningEnabled,
  sessionWarningMinutes: state.config.sessionWarningMinutes,
});

export default connect(mapStateToProps, {
  setWebsocketConnection,
  setUserAuthenticated,
  setUserData,
  notifyPanel,
  registerAppUpdate,
  logOut,
  notify,
  showSpinner,
  hideSpinner,
})(Routes);
